import { useMemo, useState } from 'react';
import { useAvatars } from '../use-avatar';
import { teamFilter } from '../use-teams';
import {
  formatHours,
  formatSort,
  leaveRequestSchema,
  leaveRequestSummarySchema,
  overtimeRequestSchema,
  overtimeRequestSummarySchema,
  requestQueueRowSchema,
  requestQueueSummarySchema,
  type LeaveBalance,
  type LeaveRequest,
  type LeaveType,
  type OvertimeRequest,
  type OvertimeRequestSummary,
  type RequestKind,
  type RequestListScope,
} from '@forecastify/shared';
import {
  DataList,
  Icon,
  Tooltip,
  Tabs,
  type DataListColumn,
  type DataListRow,
  type IconName,
  type MenuItem,
  type SortState,
  useIsMobile,
} from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { useServerList, useServerSummary } from '../use-server-list';
import { ListHeader, usePageHeader } from '../page-header';
import { leaveIcon } from './people-look';
import { identityColor, initialsOf } from '../identity';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Kérelmeim and Kérelmek — the mockup's `requests` and `wsrequests`.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **TWO TABS ON YOUR OWN PAGE, ONE LIST ON THE QUEUE — AND THE FIRST VERSION MERGED THEM.**
 * C47 put leave and overtime in one table with a *fajta* filter, which reads as one feature listed
 * twice: the two carry different columns (a leave request has working days and a frame year, a
 * claim has hours × a multiplier and an accounted total), so a shared table can only show the union
 * with half of it empty. The mockup splits them with `reqTabs`, and each tab is its own `DataList`
 * with its own columns, filters and saved views.
 *
 * ⚠️ **The QUEUE is a single list, because an approver works through *what is waiting for me*
 * rather than through two screens that might each be empty.** There the kind is a filter and the
 * person is a column — `wsReqColumns` — which is the mockup's own arrangement.
 *
 * ⚠️ **Every figure is the server's.** Days come from C44's kernel, the accounted hours carry the
 * frozen multiplier (P3-5), and the balance cards are `/leave-requests/balances`. Nothing here
 * computes a number that another screen could disagree with.
 */

/**
 * ⚠️⚠️ **THE `DataList` LABELS ARE LOCAL, AND IMPORTING THEM FROM `clients-screen` BROKE THREE
 * ROUTES.** That module is a screen: pulling one helper out of it drags the client dialog, the
 * metafield section and the page-header context into this module's graph, and the cycle that
 * produced left `useI18n` reading a context from a second module instance — *"useI18n was called
 * outside I18nProvider"* on a screen that renders inside the provider. Fourteen strings are cheaper
 * than a shared import that reaches a screen.
 */
export function listLabels(t: Translate) {
  return {
    search: t('list.search'),
    filter: t('list.filter'),
    views: t('list.views'),
    columns: t('list.columns'),
    clearAll: t('list.clearAll'),
    searchOptions: t('list.searchOptions'),
    clearFilter: (label: string) => t('list.clearFilter', { label }),
    rangeFrom: t('list.rangeFrom'),
    rangeTo: t('list.rangeTo'),
    switchView: t('list.switchView'),
    more: t('list.more'),
    back: t('list.back'),
    close: t('common.close'),
    table: t('list.table'),
    selectAll: t('list.selectAll'),
    selectRow: t('list.selectRow'),
    selectedCount: (count: number) => t('list.selectedCount', { count }),
    clearSelection: t('list.clearSelection'),
    rowMenu: t('list.rowMenu'),
    sortBy: (label: string) => t('list.sortBy', { label }),
    loading: t('state.loading'),
    loadMore: t('list.loadMore'),
    /* ⚠️⚠️ **CHEVRONS NOW, AND THE NOTE THAT STOOD HERE ARGUED FOR WORDS.** It was right that a
       bare chevron pair is announced as *"button, button"* — so the words moved to `aria-label`
       rather than disappearing, and the space they took is where item 20's page selector sits. */
    previousPage: t('list.previousPage'),
    nextPage: t('list.nextPage'),
    pageSelector: t('list.pageSelector'),
    pageOf: (page: number, count: number) => t('list.pageOf', { page, count }),
    /* ⚠️ **"találat", not a bare number.** The figure counts what the filters left rather than what
       the table holds, and the word is the only place that reading is carried. */
    pageTotal: (total: number) => t('list.pageTotal', { total }),
    loadingMore: t('list.loadingMore'),
  };
}

export function statusLabel(t: Translate, status: LeaveRequest['status']): string {
  /* ⚠️ One `t()` per branch with a literal key. */
  if (status === 'approved') return t('leave.status.approved');
  if (status === 'rejected') return t('leave.status.rejected');
  if (status === 'revoked') return t('leave.status.revoked');
  return t('leave.status.pending');
}

/** The mockup's `LEAVE_ST` badge variants. */
function statusVariant(status: LeaveRequest['status']): 'default' | 'secondary' | 'outline' {
  if (status === 'pending') return 'default';
  if (status === 'approved') return 'secondary';
  return 'outline';
}

/**
 * The saved views every request list carries — `viewsOf` in the mockup.
 *
 * ⚠️ 31 Oct 2026 · **the counts are the server's** (`/…/summary`): the list holds one page, and a
 * tab counted from it would say *50* about a set of 3,000. Every filter but the status applies, so a
 * tab's number is what choosing it would list. No figure until it arrives — `state-rules.md` §1.
 */
function statusViews(
  t: Translate,
  summary: {
    readonly total: number;
    readonly byStatus: Readonly<Record<LeaveRequest['status'], number>>;
  } | null,
): { value: string; label: string; count?: number }[] {
  const count = (value: number | undefined) => (value === undefined ? {} : { count: value });
  return [
    { value: 'all', label: t('leave.view.all'), ...count(summary?.total) },
    { value: 'pending', label: t('leave.status.pending'), ...count(summary?.byStatus.pending) },
    { value: 'approved', label: t('leave.status.approved'), ...count(summary?.byStatus.approved) },
    { value: 'rejected', label: t('leave.status.rejected'), ...count(summary?.byStatus.rejected) },
    { value: 'revoked', label: t('leave.status.revoked'), ...count(summary?.byStatus.revoked) },
  ];
}

/** The queue's kind for an id — the route needs it to send a decision or a withdrawal. */
export interface RequestSelection {
  readonly leaveIds: readonly string[];
  readonly overtimeIds: readonly string[];
}

export interface RequestsHandlers {
  readonly onNew: () => void;
  /**
   * ⚠️⚠️ **THE SECOND KIND OF REQUEST THIS PAGE LISTS — 15 Sep.** The overtime tab had no
   * create control at all: you had to know to open Időrögzítés, find the row and use its kebab
   * menu. The shape is still the server's — a claim is one worklog — so the dialog is a picker
   * over hours that already exist rather than a form somebody types an hour count into.
   */
  readonly onNewClaim: () => void;
  readonly onOpen: (id: string) => void;
  readonly onOpenTask: (key: string) => void;
  /**
   * ⚠️ The selection split by kind HERE, because only the screen holds the page the ids came from
   * — the route used to split them over both whole lists, which it no longer reads.
   */
  readonly onDecide: (selection: RequestSelection, decision: 'approved' | 'rejected') => void;
  /** A leave request or an overtime claim, and which. */
  readonly onRevoke: (request: {
    readonly id: string;
    readonly version: number;
    readonly kind: RequestKind;
  }) => void;
}

export interface PersonLook {
  readonly id: string;
  readonly name: string;
  /** An object URL for their picture, when they have one. */
  readonly avatar?: string | undefined;
  /** ⚠️ C68 · the generated face's seed, drawn under the picture. */
  readonly seed?: string | null | undefined;
}

/**
 * The three figures over Kérelmeim's *Túlóra* tab — the same strip the Erőforrás dashboard draws
 * its six in: label over number, dividers between, no cards.
 *
 * ⚠️ 31 Oct 2026 · **summed by the server** (`/overtime/summary`) over every one of the caller's
 * claims — they were summed here from the whole list, which the tab no longer holds. A rejected or
 * revoked claim is overtime nobody owes, so *E hónap* leaves them out. Dashes until it arrives.
 */
function OvertimeFigures({ hours }: { readonly hours: OvertimeRequestSummary['hours'] | null }) {
  const t = useT();
  const shown = (value: string | undefined): string =>
    value === undefined ? '–' : formatHours(Number(value));
  const figures = [
    { key: 'year', label: t('leave.ot.approvedYear'), value: shown(hours?.approvedYear) },
    { key: 'pending', label: t('leave.status.pending'), value: shown(hours?.pending) },
    { key: 'month', label: t('leave.ot.thisMonth'), value: shown(hours?.thisMonth) },
  ];
  return (
    <div className="grid shrink-0 grid-cols-3 border-b">
      {figures.map((figure) => (
        <div key={figure.key} className="border-r px-4 py-2.5 last:border-r-0">
          <div className="text-muted-foreground truncate text-[11.5px]">{figure.label}</div>
          <div className="mt-0.5 text-[19px] font-medium tabular-nums">{figure.value}</div>
        </div>
      ))}
    </div>
  );
}

export function RequestsScreen(props: {
  /**
   * ⚠️⚠️ **WHICH OF THE THREE LISTS THIS IS.** `mine` draws the personal view with its balance
   * panel; `decide` and `all` draw the same QUEUE over different data — the lead's own reviews, and
   * the whole workspace. The layout branches on personal-or-queue, the heading and the screen
   * marker on all three.
   */
  readonly listScope: RequestListScope;
  /** R-60 · the queues' team filter, server-side. Absent on the personal list. */
  readonly teams?: readonly { readonly id: string; readonly name: string }[];
  readonly teamId?: string;
  readonly onTeam?: (value: string) => void;
  /** 16 Oct 2026 · which tab opens first — Kezdőlap's *Túlóra* card links straight to it. */
  readonly initialTab?: 'leave' | 'overtime';
  /** Until the first answer: the lists draw skeleton rows and no figures. */
  readonly loading?: boolean;
  readonly canRequest: boolean;
  /**
   * ⚠️ **The roster, only so a row can show a face.** A request carries a name and nothing else,
   * and an avatar column of identical grey discs is worse than no column — reported exactly that
   * way. The colour is derived from the id (`people-look.ts`); the picture comes from here.
   */
  readonly people: readonly PersonLook[];
  /**
   * 31 Oct 2026 · the route's reload counter — a write or a colleague's change re-reads the page
   * being looked at (`use-server-list.ts`). The lists themselves are read here, one page at a time.
   */
  readonly nonce: number;
  readonly balances: readonly LeaveBalance[];
  readonly types: readonly LeaveType[];
  readonly serverError: string | null;
  readonly handlers: RequestsHandlers;
}) {
  const mobile = useIsMobile();
  const t = useT();
  const [tab, setTab] = useState<'leave' | 'overtime'>(props.initialTab ?? 'leave');

  /**
   * ⚠️⚠️ **THE CTA BELONGS IN THE PAGE HEADER, NOT IN THE TOOLBAR.** The mockup puts a filled
   * *Új szabadságkérelem* at the top right of the screen, which is where every other create button
   * in the product sits (`ListHeader`'s `action`). Ours was an outline button inside the list
   * toolbar, so the one thing this screen is for read as a filter control.
   */
  usePageHeader(
    () => (
      /**
       * ┌───────────────────────────────────────────────────────────────────────────┐
       * │ ⚠️⚠️ NO ACTION IN THE HEADER — THE CREATE IS IN THE TOOLBAR. 15 Sep.      │
       * └───────────────────────────────────────────────────────────────────────────┘
       *
       * This screen is a LIST: a toolbar, a search, saved views, a table. The settled shape for
       * those is an outline button in line with the search — *"one line with the search, not
       * purple"* — and the filled header button that stood here was the SETTINGS shape applied to
       * a list. Both shapes are right; batch 1's note at the metafields call site argues which is
       * which, and this is the other half of it.
       *
       * ⚠️ **Each tab passes its own action to its own `DataList`**, so the control that makes an
       * overtime claim is inside the overtime list rather than above both of them.
       */
      <ListHeader
        title={
          props.listScope === 'mine'
            ? t('title.requests')
            : props.listScope === 'decide'
              ? t('title.toDecide')
              : t('title.approvals')
        }
      />
    ),
    /* ⚠️ VALUES ONLY — `page-header.tsx`'s loop. The handler is reached through the ref. */
    [props.listScope, t],
  );

  const [leaveView, setLeaveView] = useState('all');
  const [leaveSort, setLeaveSort] = useState<readonly SortState[]>([]);

  const [otSort, setOtSort] = useState<readonly SortState[]>([]);
  const [queueSort, setQueueSort] = useState<readonly SortState[]>([]);
  const [leaveQuery, setLeaveQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');

  const [otView, setOtView] = useState('all');
  const [otQuery, setOtQuery] = useState('');
  const [otTypeFilter, setOtTypeFilter] = useState('');

  /**
   * ┌────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THE TWO QUEUES OPEN ON DIFFERENT VIEWS, BECAUSE THEY ASK DIFFERENT THINGS.  │
   * └────────────────────────────────────────────────────────────────────┘
   *
   * *Elbírálandó kérelmek* is a work list — *what is waiting for me* — so it opens on **Függőben**,
   * and a decided request has left it as far as the reader is concerned.
   *
   * *Kérelmek* is the workspace's record. Somebody opens it to see what HAPPENED as often as to see
   * what is outstanding, and opening on one status hides most of it behind a control they have to
   * find first — so it opens on **Minden kérelem**. Asked for on 20 Sep.
   */
  const [queueView, setQueueView] = useState(props.listScope === 'all' ? 'all' : 'pending');
  const [queueQuery, setQueueQuery] = useState('');
  const [kindFilter, setKindFilter] = useState('');

  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ 31 Oct 2026 · ONE PAGE OF EACH LIST, ASKED OF THE SERVER WITH ITS FILTERS.│
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * The lists were every request the reader could see — 3,276 on UAT — filtered, sorted and paged
   * here. Each now asks for its page (`?status&search&sort&offset`), and the tabs' counts, the
   * year and type filters' options and the overtime figures come from its summary. ⚠️ **A list
   * the layout does not draw reads nothing**: the personal page reads its two tabs, a queue its one.
   */
  const personal = props.listScope === 'mine';
  const team = props.teamId ?? '';
  const leaveFilters = {
    scope: 'mine',
    leaveTypeId: typeFilter,
    year: yearFilter,
    search: leaveQuery.trim(),
  };
  const leaveList = useServerList({
    path: personal ? '/leave-requests' : null,
    params: {
      ...leaveFilters,
      status: leaveView === 'all' ? '' : leaveView,
      sort: formatSort(leaveSort) ?? '',
    },
    schema: leaveRequestSchema,
    nonce: props.nonce,
  });
  const leaveSummary = useServerSummary({
    path: personal ? '/leave-requests/summary' : null,
    params: leaveFilters,
    schema: leaveRequestSummarySchema,
    nonce: props.nonce,
  });

  const otFilters = { scope: 'mine', overtimeTypeId: otTypeFilter, search: otQuery.trim() };
  const otList = useServerList({
    path: personal ? '/overtime' : null,
    params: {
      ...otFilters,
      status: otView === 'all' ? '' : otView,
      sort: formatSort(otSort) ?? '',
    },
    schema: overtimeRequestSchema,
    nonce: props.nonce,
  });
  const otSummary = useServerSummary({
    path: personal ? '/overtime/summary' : null,
    params: otFilters,
    schema: overtimeRequestSummarySchema,
    nonce: props.nonce,
  });

  const queueFilters = {
    scope: props.listScope,
    teamId: team,
    kind: kindFilter,
    search: queueQuery.trim(),
  };
  const queueList = useServerList({
    path: personal ? null : '/requests',
    params: {
      ...queueFilters,
      status: queueView === 'all' ? '' : queueView,
      sort: formatSort(queueSort) ?? '',
    },
    schema: requestQueueRowSchema,
    nonce: props.nonce,
  });
  const queueSummary = useServerSummary({
    path: personal ? null : '/requests/summary',
    params: queueFilters,
    schema: requestQueueSummarySchema,
    nonce: props.nonce,
  });

  /* Every request and claim on screen, by id — the row menu, the bulk decision and the faces. */
  const shownRequests: readonly LeaveRequest[] = personal
    ? leaveList.rows
    : queueList.rows.flatMap((row) => (row.kind === 'leave' ? [row.leave] : []));
  const shownClaims: readonly OvertimeRequest[] = personal
    ? otList.rows
    : queueList.rows.flatMap((row) => (row.kind === 'overtime' ? [row.overtime] : []));

  /* ⚠️ Through the resolver: `leave_types.icon` is free text and `Icon` is a closed set, so a
     name it does not know must fall back rather than render nothing (`people-look.ts`). */
  const iconOf = (leaveTypeId: string): IconName =>
    leaveIcon(props.types.find((type) => type.id === leaveTypeId)?.icon);

  /**
   * One person, as a cell: their picture where we have one, their initials on their own identity
   * colour where we do not.
   *
   * ⚠️ `memberId` is `null` for a name we only know as text — a decider on a request whose id the
   * row does not carry. The colour is then derived from the name, which is stable enough for a
   * disc and honest about what we know.
   */
  const personCell = (memberId: string | null, name: string) => {
    const person = memberId === null ? undefined : props.people.find((one) => one.id === memberId);
    return {
      avatar: {
        initials: initialsOf(name),
        ...(person?.seed === undefined || person.seed === null ? {} : { seed: person.seed }),
        color: identityColor(memberId ?? name),
        title: name,
        ...(person?.avatar === undefined ? {} : { src: person.avatar }),
      },
      text: name,
      bold: true,
    };
  };

  /**
   * 27 Oct 2026 · the DECIDER's face, which the row carries itself — `props.people` is the
   * requesters, and a decider is often nobody else on the list. Their picture where they have one,
   * else their Blobatar; initials only where neither is known.
   */
  const deciderFaces = useAvatars([
    ...shownRequests.map((one) => one.decidedByAvatarUrl),
    ...shownClaims.map((one) => one.approverAvatarUrl),
  ]);
  const deciderCell = (name: string, seed: string | null, url: string | null) => {
    const src = url === null ? undefined : deciderFaces.get(url);
    return {
      avatar: {
        initials: initialsOf(name),
        ...(seed === null ? {} : { seed }),
        color: identityColor(name),
        title: name,
        ...(src === undefined ? {} : { src }),
      },
      text: name,
      bold: false,
      muted: true,
    };
  };

  /* ── the rows ─────────────────────────────────────────────────────────────────────────── */

  const leaveRow = (request: LeaveRequest, withPerson: boolean): DataListRow => ({
    id: request.id,
    cells: {
      ...(withPerson ? { who: personCell(request.memberId, request.memberName) } : {}),
      code: { text: request.identifier, mono: true, muted: true },
      type: { icon: iconOf(request.leaveTypeId), text: request.leaveTypeName },
      period: { text: `${request.fromDate} – ${request.toDate}` },
      days: { text: t('leave.amount.days', { days: request.days }), mono: true },
      year: { text: String(request.year), mono: true, muted: true },
      status: {
        badge: { label: statusLabel(t, request.status), variant: statusVariant(request.status) },
      },
      created: { text: request.createdAt.slice(0, 10), mono: true, muted: true },
      decided:
        request.decidedByName === null
          ? { text: '–', muted: true }
          : deciderCell(
              request.decidedByName,
              request.decidedByAvatarSeed,
              request.decidedByAvatarUrl,
            ),
    },
  });

  const overtimeRow = (claim: OvertimeRequest, withPerson: boolean): DataListRow => ({
    id: claim.id,
    cells: {
      ...(withPerson ? { who: personCell(claim.memberId, claim.memberName) } : {}),
      code: { text: claim.identifier, mono: true, muted: true },
      /* ⚠️ An em-dash until an approver says which kind — `0147`. */
      type: { icon: 'timer', text: claim.overtimeTypeName ?? '–' },
      period: { text: `${claim.workedOn} · ${claim.taskKey ?? '–'}` },
      /**
       * ⚠️⚠️ Hours × the multiplier, then the accounted total — the two figures a claim IS, and
       * the reason it cannot share a column with a leave request's working days.
       *
       * ⚠️⚠️ **THE HOURS ALONE UNTIL IT IS PRICED — `0147`.** A pending row read
       * *Függőben · 2×* off the type's CURRENT rate, which is a price beside a claim nobody had
       * decided — and one that moved if somebody edited the type.
       */
      amount: {
        text:
          claim.multiplier === null
            ? formatHours(Number(claim.hours))
            : `${formatHours(Number(claim.hours))} × ${Number(claim.multiplier).toLocaleString()}`,
        mono: true,
      },
      accounted: {
        text: claim.accountedHours === null ? '–' : formatHours(Number(claim.accountedHours)),
        mono: true,
        bold: claim.status === 'approved',
        muted: claim.status !== 'approved',
      },
      status: {
        badge: { label: statusLabel(t, claim.status), variant: statusVariant(claim.status) },
      },
      approver:
        claim.approverName === null
          ? { text: '–', muted: true }
          : deciderCell(claim.approverName, claim.approverAvatarSeed, claim.approverAvatarUrl),
    },
  });

  /* ── the row menu — the mockup's `reqMenu` ────────────────────────────────────────────── */

  const menuFor = (id: string): MenuItem[] => {
    const request = shownRequests.find((row) => row.id === id);
    const claim = shownClaims.find((row) => row.id === id);
    const status = request?.status ?? claim?.status;
    if (status === undefined) return [];

    const items: MenuItem[] = [
      {
        label: t('leave.menu.open'),
        icon: 'arrow-right',
        onSelect: () => {
          props.handlers.onOpen(id);
        },
      },
    ];

    if (claim?.taskKey != null) {
      const key = claim.taskKey;
      items.push({
        label: t('leave.menu.openTask'),
        icon: 'list-checks',
        onSelect: () => {
          props.handlers.onOpenTask(key);
        },
      });
    }

    const canDecide = request?.canDecide === true || claim?.canDecide === true;
    if (canDecide && status === 'pending') {
      items.push(
        { type: 'separator' },
        {
          label: t('leave.action.approve'),
          icon: 'circle-check',
          onSelect: () => {
            props.handlers.onDecide(selectionOf([id]), 'approved');
          },
        },
        {
          label: t('leave.action.reject'),
          icon: 'x',
          onSelect: () => {
            props.handlers.onDecide(selectionOf([id]), 'rejected');
          },
        },
      );
    }

    /* ⚠️ Revoking is the REQUESTER's act and works on a pending OR an approved request — a holiday
       nobody wants any more is withdrawn rather than rejected. */
    const own =
      request?.canRevoke === true
        ? { id: request.id, version: request.version, kind: 'leave' as const }
        : claim?.canRevoke === true
          ? { id: claim.id, version: claim.version, kind: 'overtime' as const }
          : null;
    if (own !== null) {
      items.push(
        { type: 'separator' },
        {
          label: t('leave.action.revoke'),
          icon: 'flag',
          onSelect: () => {
            props.handlers.onRevoke(own);
          },
        },
      );
    }
    return items;
  };

  /* ── the three lists ──────────────────────────────────────────────────────────────────── */

  /* ⚠️ Filtered, sorted and paged by the server — these only draw the page that came back. */
  const leaveRows = useMemo(
    () => leaveList.rows.map((request) => leaveRow(request, false)),
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `leaveRow` closes over `t` and the
       type list, both of which change with the props this already depends on. */
    [leaveList.rows, props.types, t],
  );

  const otRows = useMemo(
    () => otList.rows.map((claim) => overtimeRow(claim, false)),
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- as above. */
    [otList.rows, t],
  );

  const queueRows = useMemo(
    () =>
      queueList.rows.map((row) =>
        row.kind === 'leave' ? leaveRow(row.leave, true) : overtimeRow(row.overtime, true),
      ),
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- as above. */
    [queueList.rows, props.types, t],
  );

  /* ── the columns ──────────────────────────────────────────────────────────────────────── */

  const leaveColumns: DataListColumn[] = [
    { key: 'code', label: t('list.identifier'), width: 120, sortKey: 'code' },
    { key: 'type', label: t('leave.column.type'), width: 200, sortKey: 'type' },
    { key: 'period', label: t('leave.column.period'), width: 190, sortKey: 'period' },
    { key: 'days', label: t('leave.column.days'), width: 104, align: 'right', sortKey: 'days' },
    { key: 'year', label: t('leave.column.year'), width: 130, sortKey: 'year' },
    { key: 'status', label: t('leave.column.status'), width: 120, sortKey: 'status' },
    { key: 'created', label: t('leave.column.created'), width: 108, sortKey: 'created' },
    { key: 'decided', label: t('leave.column.decidedBy'), width: 168, sortKey: 'decided' },
  ];

  const otColumns: DataListColumn[] = [
    { key: 'code', label: t('list.identifier'), width: 120, sortKey: 'code' },
    { key: 'type', label: t('leave.column.type'), width: 180, sortKey: 'type' },
    { key: 'period', label: t('leave.column.dayAndTask'), width: 210, sortKey: 'period' },
    {
      key: 'amount',
      label: t('leave.column.hoursTimes'),
      width: 150,
      align: 'right',
      sortKey: 'amount',
    },
    {
      key: 'accounted',
      label: t('leave.column.accounted'),
      width: 110,
      align: 'right',
      sortKey: 'accounted',
    },
    { key: 'status', label: t('leave.column.status'), width: 120, sortKey: 'status' },
    { key: 'approver', label: t('leave.column.decidedBy'), width: 168, sortKey: 'approver' },
  ];

  /* ⚠️ The queue's own shape: the person first, then everything a leave row carries, plus the two
     columns a claim needs. A row of one kind simply has no cell for the other's columns. */
  const queueColumns: DataListColumn[] = [
    { key: 'code', label: t('list.identifier'), width: 120, sortKey: 'code' },
    { key: 'who', label: t('leave.column.person'), width: 180, sortKey: 'who' },
    { key: 'type', label: t('leave.column.type'), width: 170, sortKey: 'type' },
    { key: 'period', label: t('leave.column.period'), width: 200, sortKey: 'period' },
    { key: 'days', label: t('leave.column.days'), width: 96, align: 'right', sortKey: 'days' },
    {
      key: 'amount',
      label: t('leave.column.hoursTimes'),
      width: 130,
      align: 'right',
      sortKey: 'amount',
    },
    { key: 'status', label: t('leave.column.status'), width: 116, sortKey: 'status' },
    { key: 'created', label: t('leave.column.created'), width: 104, sortKey: 'created' },
    { key: 'decided', label: t('leave.column.decidedBy'), width: 150, sortKey: 'decided' },
  ];

  /* The ids on this page that may be decided, by kind — the route sends each kind its own POST. */
  function selectionOf(ids: readonly string[]): RequestSelection {
    return {
      leaveIds: ids.filter((id) => shownRequests.find((row) => row.id === id)?.canDecide === true),
      overtimeIds: ids.filter((id) => shownClaims.find((row) => row.id === id)?.canDecide === true),
    };
  }

  /* ⚠️ The server's list of frame years, counted without the year filter itself. */
  const years = leaveSummary?.years ?? [];

  /* ── the screen ───────────────────────────────────────────────────────────────────────── */

  if (props.listScope !== 'mine') {
    return (
      /* ⚠️ A marker each: `routes.test.tsx` proves an address drew its OWN screen, and two routes
         sharing one marker would let either satisfy the gate for both. */
      <div
        data-screen={props.listScope === 'decide' ? 'to-decide' : 'approvals'}
        className="flex min-h-0 flex-1 flex-col"
      >
        {props.serverError === null ? null : (
          <p className="text-destructive px-4 pt-2 text-[12px]">{props.serverError}</p>
        )}
        <DataList
          columnsKey="requests"
          card={{
            title: 'who',
            context: 'code',
            defaults: { badge: 'status', details: ['type', 'period', 'days', 'created'] },
          }}
          columns={queueColumns}
          rows={queueRows}
          isLoading={props.loading === true || queueList.loading}
          page={queueList.page}
          sort={queueSort}
          onSortChange={setQueueSort}
          labels={listLabels(t)}
          search={queueQuery}
          onSearchChange={setQueueQuery}
          views={statusViews(t, queueSummary)}
          view={queueView}
          onViewChange={setQueueView}
          filters={[
            {
              key: 'kind',
              label: t('leave.filter.kind'),
              value: kindFilter,
              onChange: setKindFilter,
              options: [
                { value: '', label: t('leave.filter.allKinds') },
                { value: 'leave', label: t('leave.kind.leave') },
                { value: 'overtime', label: t('leave.kind.overtime') },
              ],
            },
            ...(props.onTeam === undefined
              ? []
              : teamFilter(t, props.teams ?? [], props.teamId ?? '', props.onTeam)),
          ]}
          rowMenu={menuFor}
          onRowClick={props.handlers.onOpen}
          bulkActions={[
            {
              label: t('leave.action.approve'),
              icon: 'circle-check',
              onSelect: (ids) => {
                props.handlers.onDecide(selectionOf(ids), 'approved');
              },
            },
            {
              label: t('leave.action.reject'),
              icon: 'x',
              destructive: true,
              onSelect: (ids) => {
                props.handlers.onDecide(selectionOf(ids), 'rejected');
              },
            },
          ]}
          empty={{
            icon: 'inbox',
            title: t('leave.empty.queue'),
            body: t('leave.empty.queueBody'),
          }}
        />
      </div>
    );
  }

  return (
    <div data-screen="requests" className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b px-4">
        <Tabs
          variant="underline"
          className="flex-1 border-0"
          label={t('title.requests')}
          value={tab}
          onValueChange={(value) => {
            setTab(value === 'overtime' ? 'overtime' : 'leave');
          }}
          items={[
            {
              value: 'leave',
              label: t('leave.kind.leave'),
              icon: 'sun',
              /* ⚠️ No figure while loading — `state-rules.md` §1. */
              ...(props.loading === true || leaveSummary === null
                ? {}
                : { count: leaveSummary.total }),
            },
            { value: 'overtime', label: t('leave.kind.overtime'), icon: 'timer' },
          ]}
        />
      </div>

      {props.serverError === null ? null : (
        <p className="text-destructive px-4 pt-2 text-[12px]">{props.serverError}</p>
      )}

      {tab === 'leave' ? (
        <div className="flex min-h-0 flex-1 flex-col">
          {/* ── the balance cards, with the mockup's two-segment bar ──────────────────────── */}
          {props.balances.length === 0 ? null : (
            <div
              /* ⚠️ 17 Oct 2026 · on a phone: ONE row of two, swiped two at a time — the product
                 owner. Every other card is a snap point that cannot be skipped. */
              className={
                mobile
                  ? 'scrollbar-none flex snap-x snap-mandatory items-stretch gap-3 overflow-x-auto border-b p-4'
                  : 'flex flex-wrap items-stretch gap-3 border-b p-4'
              }
              data-tour="request-balances"
            >
              {props.balances.map((balance, index) => {
                const frame = balance.quotaDays ?? 0;
                const available = Math.max(0, frame - balance.usedDays - balance.pendingDays);
                const pct = (value: number): number =>
                  frame === 0 ? 0 : Math.max(0, Math.min(100, Math.round((value / frame) * 100)));
                const card = (
                  <div
                    key={balance.leaveTypeId}
                    /* ⚠️ Focusable while it carries the explanation, so a keyboard reaches it too. */
                    tabIndex={balance.quotaDays === null ? undefined : 0}
                    className={
                      mobile
                        ? `bg-card w-[calc(50%-6px)] min-w-0 shrink-0 rounded-xl border p-3 shadow-sm ${
                            index % 2 === 0 ? 'snap-start [scroll-snap-stop:always]' : ''
                          }`
                        : 'bg-card min-w-[240px] flex-1 rounded-xl border p-3 shadow-sm'
                    }
                  >
                    <div className="flex items-center gap-2">
                      <Icon
                        name={iconOf(balance.leaveTypeId)}
                        size={14}
                        className="text-muted-foreground"
                      />
                      <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium">
                        {balance.leaveTypeName}
                      </span>
                      {/* ⚠️ Three figures, in the mockup's own order: what is left, what is
                          waiting on a decision, and the frame. A balance that showed only the
                          first would let somebody book the same fortnight twice. */}
                      <span className="text-[13px] font-medium tabular-nums">
                        {balance.quotaDays === null
                          ? t('leave.balance.unlimited')
                          : `${String(available)} / ${String(balance.pendingDays)} / ${String(frame)}`}
                      </span>
                    </div>
                    {balance.quotaDays === null ? null : (
                      <div className="bg-muted mt-2 flex h-2 overflow-hidden rounded-full">
                        <span
                          className="bg-primary h-full"
                          style={{ width: `${String(pct(available))}%` }}
                        />
                        <span
                          className="h-full"
                          style={{
                            width: `${String(pct(balance.pendingDays))}%`,
                            background: 'var(--status-warn)',
                          }}
                        />
                      </div>
                    )}
                    {balance.carriedDays === 0 ? null : (
                      <p className="text-muted-foreground mt-1.5 text-[11px]">
                        {balance.carriedExpiresOn === null
                          ? t('leave.balance.carried', { days: balance.carriedDays })
                          : t('leave.balance.carriedUntil', {
                              days: balance.carriedDays,
                              date: balance.carriedExpiresOn,
                            })}
                      </p>
                    )}
                  </div>
                );
                /**
                 * ⚠️ 27 Oct 2026 · *"25 / 0 / 30"* says nothing on its own, so a card with a frame
                 * names its figures on hover: what is left, what waits on a decision, what is
                 * already approved, and the frame. Unlimited has one word and needs none.
                 */
                return balance.quotaDays === null ? (
                  card
                ) : (
                  <Tooltip
                    key={balance.leaveTypeId}
                    asChild
                    side="bottom"
                    content={
                      /* The meaning in bold, the figure plain — the product owner. */
                      <span className="flex flex-col gap-0.5 tabular-nums">
                        {(
                          [
                            ['leave.balance.tip.available', available],
                            ['leave.balance.tip.pending', balance.pendingDays],
                            ['leave.balance.tip.used', balance.usedDays],
                            ['leave.balance.tip.frame', frame],
                          ] as const
                        ).map(([key, days]) => (
                          <span key={key}>
                            <span className="font-semibold">{t(key)}:</span>{' '}
                            {t('leave.balance.tip.days', { days })}
                          </span>
                        ))}
                      </span>
                    }
                  >
                    {card}
                  </Tooltip>
                );
              })}
            </div>
          )}

          <DataList
            columnsKey="my-leave"
            card={{
              title: 'type',
              context: 'code',
              defaults: { badge: 'status', details: ['period', 'days', 'created', 'decided'] },
            }}
            /* ⚠️ Outline, in line with the search — the list screens' settled shape. ⚠️ The scope
               is already `mine` here: both queues return above, which is why it is not re-checked. */
            {...(props.canRequest
              ? {
                  actions: [
                    {
                      label: t('leave.new'),
                      icon: 'plus' as const,
                      primary: true,
                      onSelect: props.handlers.onNew,
                    },
                  ],
                }
              : {})}
            columns={leaveColumns}
            rows={leaveRows}
            isLoading={props.loading === true || leaveList.loading}
            page={leaveList.page}
            sort={leaveSort}
            onSortChange={setLeaveSort}
            labels={listLabels(t)}
            search={leaveQuery}
            onSearchChange={setLeaveQuery}
            views={statusViews(t, leaveSummary)}
            view={leaveView}
            onViewChange={setLeaveView}
            filters={[
              {
                key: 'type',
                label: t('leave.column.type'),
                value: typeFilter,
                onChange: setTypeFilter,
                options: [
                  { value: '', label: t('leave.filter.allTypes') },
                  ...props.types.map((type) => ({ value: type.id, label: type.name })),
                ],
              },
              {
                key: 'year',
                label: t('leave.column.year'),
                value: yearFilter,
                onChange: setYearFilter,
                options: [
                  { value: '', label: t('leave.filter.allYears') },
                  ...years.map((year) => ({ value: String(year), label: String(year) })),
                ],
              },
            ]}
            rowMenu={menuFor}
            onRowClick={props.handlers.onOpen}
            empty={{
              icon: 'sun',
              title: t('leave.empty.mine'),
              body: t('leave.empty.mineBody'),
              ...(props.canRequest
                ? { actionLabel: t('leave.new'), onAction: props.handlers.onNew }
                : {}),
            }}
          />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          {/**
           * ┌───────────────────────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ 16 Oct 2026 · THREE FIGURES ARE BACK, AS A STRIP, NOT CARDS.      │
           * └───────────────────────────────────────────────────────────────────────┘
           *
           * The 15 Sep removal took three CARDS (*Elfogadott túlóra — e hónap*, *Függőben*,
           * *Elbírálásra vár nálam*). The reworked homescreen mockup brings figures back with
           * different questions — *Elfogadva — idén*, every pending claim, *E hónap* — and the
           * product owner asked for them drawn as the Erőforrás dashboard's strip of blocks, with
           * no small print under the numbers. *Elbírálásra vár nálam* stays gone: it is a
           * question about other people's claims, and this is your own list.
           */}
          <OvertimeFigures hours={otSummary?.hours ?? null} />
          <DataList
            columnsKey="my-overtime"
            card={{
              title: 'type',
              context: 'code',
              defaults: { badge: 'status', details: ['period', 'amount', 'accounted', 'approver'] },
            }}
            actions={[
              {
                label: t('leave.claim.new'),
                icon: 'plus',
                primary: true,
                onSelect: props.handlers.onNewClaim,
              },
            ]}
            columns={otColumns}
            rows={otRows}
            isLoading={props.loading === true || otList.loading}
            page={otList.page}
            sort={otSort}
            onSortChange={setOtSort}
            labels={listLabels(t)}
            search={otQuery}
            onSearchChange={setOtQuery}
            views={statusViews(t, otSummary)}
            view={otView}
            onViewChange={setOtView}
            filters={[
              {
                key: 'ottype',
                label: t('leave.column.type'),
                value: otTypeFilter,
                onChange: setOtTypeFilter,
                options: [
                  { value: '', label: t('leave.filter.allTypes') },
                  /* ⚠️ The types the reader's claims carry, from the server — an undecided claim
                     has none to filter by, and a page cannot know the rest. */
                  ...(otSummary?.types ?? []).map((type) => ({
                    value: type.id,
                    label: type.name,
                  })),
                ],
              },
            ]}
            rowMenu={menuFor}
            onRowClick={props.handlers.onOpen}
            empty={{
              icon: 'timer',
              title: t('leave.empty.overtime'),
              /* ⚠️ It still says where a claim comes FROM — one logged hour — because that is the
                 shape the server takes. What changed is that the picker is now on this page too. */
              body: t('leave.empty.overtimeBody'),
              actionLabel: t('leave.claim.new'),
              onAction: props.handlers.onNewClaim,
            }}
          />
        </div>
      )}
    </div>
  );
}
